import React, { memo, useCallback, useEffect, useRef } from 'react';
import { noop } from 'lodash';
import { TouchableOpacity, View, Pressable, Keyboard } from 'react-native';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Spacer from 'design-system/Spacer';
import { useClearableTimeout } from 'hooks/useClearableTimeout';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import ToolTipContent from './ToolTipContent';
import IconTooltip from './icons/IconTooltip';
import { hitSlopS, rem } from './values';
import { isWeb } from '../constants';
import PopUpMenuModal from './PopUpMenuModal.web';
import ToolTipContentWeb, { toolTipWebContentWidth } from './ToolTipContentWeb';
import useDismissOnOutsidePress from './useDismissOnOutsidePress';
const BtnComp = isWeb ? Pressable : TouchableOpacity;
// The web popover is shared by every tooltip. A tooltip only hides it while it
// is still the one showing it, so a late hide can't close another tooltip's.
let popUpOwner = null;
const somePadding = rem(32);
const space = rem(20);
const HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 };
const ToolTip = ({ title, offset, subtitle, message, colorKey, centered, noBackdrop, onModalShown, onModalHidden = noop, small, learnMore, tootTipBtnText, onBtnPress, onExtraBtnPress, onPressLearnMore, toolTipExtraBtnText, extraYOffset, }) => {
    const styles = useStyles(styleSet);
    const touchViewRef = useRef(null);
    const popUpRef = useRef(null);
    const ownerId = useRef(Symbol('ToolTip')).current;
    const [isHovering, setIsHovering] = React.useState(false);
    const { deviceWidth: width } = useAppFrameDimensions();
    const [setHideModalTimeout, clearHideModalTimeout] = useClearableTimeout();
    const handleHoverIn = useCallback(() => {
        if (isWeb) {
            clearHideModalTimeout();
            setIsHovering(true);
        }
    }, [clearHideModalTimeout]);
    const handleHoverOut = useCallback(() => {
        if (isWeb) {
            clearHideModalTimeout();
            setHideModalTimeout(() => setIsHovering(false), 200);
        }
    }, [clearHideModalTimeout, setHideModalTimeout]);
    const showPopUpModal = useCallback(() => {
        if (touchViewRef) {
            touchViewRef.current?.measure((_, __, ___, ____, fx, fy) => {
                const toolXPosition = () => {
                    const halfWidth = toolTipWebContentWidth / 2;
                    const centered = fx - halfWidth;
                    if (fx + halfWidth + space > width) {
                        return fx - toolTipWebContentWidth - somePadding * 2;
                    }
                    if (fx - halfWidth < 0) {
                        return fx;
                    }
                    return centered;
                };
                popUpOwner = ownerId;
                PopUpMenuModal.show(<Pressable ref={popUpRef} onHoverIn={handleHoverIn} onHoverOut={handleHoverOut} style={styles.webModal}>
            <ToolTipContentWeb title={title} subtitle={subtitle}/>
          </Pressable>, {
                    fy: fy + rem(20) + rem(extraYOffset || 0),
                    onHoverOnly: true,
                    modalWidth: toolTipWebContentWidth,
                    fx: toolXPosition(),
                });
            });
        }
    }, [
        extraYOffset,
        handleHoverIn,
        handleHoverOut,
        ownerId,
        styles.webModal,
        subtitle,
        title,
        width,
    ]);
    const hidePopUpModal = useCallback(() => {
        if (popUpOwner === ownerId) {
            popUpOwner = null;
            PopUpMenuModal.hide();
        }
    }, [ownerId]);
    useEffect(() => {
        if (isHovering) {
            showPopUpModal();
        }
        else {
            hidePopUpModal();
        }
    }, [isHovering, showPopUpModal, hidePopUpModal]);
    useEffect(() => hidePopUpModal, [hidePopUpModal]);
    // A tap has no hover out, so on touch screens a tap elsewhere closes it
    const dismiss = useCallback(() => {
        clearHideModalTimeout();
        setIsHovering(false);
    }, [clearHideModalTimeout]);
    useDismissOnOutsidePress(isHovering, [touchViewRef, popUpRef], dismiss);
    const onPress = useCallback(() => {
        // On web a click or tap opens the same popover hover and focus open, for touch users;
        // it closes on hover out or blur
        if (isWeb) {
            clearHideModalTimeout();
            setIsHovering(true);
            return;
        }
        Keyboard.dismiss();
        onModalShown?.();
        Modal.show(<ToolTipContent subtitle={subtitle} onPress={() => {
                Modal.hide(onBtnPress, { top: true });
            }} learnMore={learnMore} btnText={tootTipBtnText || 'Okay'} extraBtnText={toolTipExtraBtnText} onExtraBtnPress={() => {
                Modal.hide(onExtraBtnPress, { top: true });
            }} onPressLearnMore={onPressLearnMore}/>, {
            title,
            top: true,
            noBackdrop,
            textStyle: styles.title,
            onDidHideCallback: onModalHidden,
        });
    }, [
        onModalShown,
        subtitle,
        learnMore,
        tootTipBtnText,
        toolTipExtraBtnText,
        onPressLearnMore,
        title,
        noBackdrop,
        styles.title,
        onModalHidden,
        onBtnPress,
        onExtraBtnPress,
        clearHideModalTimeout,
    ]);
    if (message) {
        return (<BtnComp ref={touchViewRef} onPress={onPress} hitSlop={HIT_SLOP} onHoverIn={handleHoverIn} onHoverOut={handleHoverOut} onFocus={handleHoverIn} onBlur={handleHoverOut} style={[styles.row, centered && styles.centered]}>
        <View>
          <Spacer h={2}/>
          <IconTooltip colorKey={colorKey} height={16} width={16}/>
        </View>
        <Spacer w={8}/>
        <Text TextThin-14 Gray>
          {message}
        </Text>
      </BtnComp>);
    }
    if (small) {
        return (<View>
        {!!offset && <Spacer h={offset}/>}
        <BtnComp onPress={onPress} ref={touchViewRef} hitSlop={hitSlopS} onHoverIn={handleHoverIn} onHoverOut={handleHoverOut} onFocus={handleHoverIn} onBlur={handleHoverOut}>
          <IconTooltip height={16} width={16} colorKey={colorKey} testID="icon"/>
        </BtnComp>
      </View>);
    }
    return (<BtnComp hitSlop={HIT_SLOP} onPress={onPress} ref={touchViewRef} onHoverIn={handleHoverIn} onHoverOut={handleHoverOut} onFocus={handleHoverIn} onBlur={handleHoverOut}>
      <IconTooltip colorKey={colorKey} testID="icon"/>
    </BtnComp>);
};
const styleSet = createStyleSheets(() => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    centered: {
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
    title: {
        textAlign: 'center',
    },
    webModal: {
        padding: rem(16),
        cursor: 'auto',
    },
}));
export default memo(ToolTip);
